import { Await, Link } from '@remix-run/react'
import { Suspense, useEffect, useRef, useState } from 'react'
import Filter from '#app/components/inputs/filter'
import IconButton from '../buttons/iconButton.tsx'
import MoreIcon from '../icons/Interface/MoreIcon'
import { Counter } from '#app/components/badgesAndTags'
import Avatar from '../avatars/avatar.tsx'
import PillTabPopover from '../tabs/pillTabPopover.tsx'
import { toast } from 'sonner'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyParagraph,
	TypographyLabel,
} from '#app/components/typography'
import {
	CaretDown,
	CopyIcon,
	WebsiteIcon,
	TwitterIcon,
	InstagramIcon,
	DiscordIcon,
	LocationIcon,
	OpenSeaIcon,
	ShareDotsIcon,
	PlusIcon,
} from '#app/components/icons'
import {
	getShareUserProfileToXURL,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import CollectionsTable from './collectionsTable.tsx'
import OffChain from '../common/offChain.tsx'
import ProofOfExhibition from '../common/proofOfExhibition.tsx'
import {
	artist,
	AttestationType,
	PinnedMetadataProperties,
	TargetWallet,
	User,
} from '#types/loreTypes.tsx'
import InternalLink from '../common/internalLink.tsx'
import Pagination from '../wayfinding/pagination.tsx'
import EventCard from '../content/EventCard.tsx'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import Button from '../buttons/button.tsx'
import { nft, nftCollectionInfo } from '#types/loreTypes.tsx'
import { DynamicContext, useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { ClientOnly } from '#node_modules/remix-utils/build/react/client-only.js'
import { SignModal } from '../common/signModal.tsx'
import HighlightedAttestations from '../highlightedAttestations/highlightedAttestations.tsx'
import Toast from '../toastsAndMessages/toast.tsx'

// FIXME:
// Move this type to common types and unify with all other sign modal types
type SignParamsType = {
	open: boolean
	onClose?: () => void
	template?: string
	pendingSign?: PinnedMetadataProperties
	countersignEntry?: AttestationType
	matchingAddress?: boolean
	isValidSign?: boolean
	nft?: nft
	nftCollectionInfo?: nftCollectionInfo
	contractAddress?: string
	tokenId?: string
	origin?: string
	atomicLoreUrl?: string
	targetWallet?: TargetWallet | null
}

export default function ArtistProfile({
	artist,
	stats,
	attestations: initialAttestations,
	origin,
	signModalParams,
}: {
	artist: User
	stats: {
		totalCollections: number
		totalProofOfExhibitions: number
		totalAttestations: number
	}
	attestations: Promise<AttestationType[]>
	origin: string
	signModalParams: SignParamsType
}) {
	const [signParams, setSignParams] = useState<SignParamsType>({ open: false })
	const [highlightedAttestations, setHighlightedAttestations] = useState<
		AttestationType[]
	>([])
	const [attestations, setAttestations] = useState<AttestationType[]>([])

	// Fetch highlighted attestations when component mounts
	useEffect(() => {
		const fetchHighlightedAttestations = async () => {
			try {
				const response = await fetch(
					`/attestationHighlightsAPI?userId=${artist.id}`,
				)
				if (!response.ok) {
					throw new Error('Failed to fetch highlighted attestations')
				}
				const data = await response.json()
				setHighlightedAttestations(data.highlightedAttestations)
			} catch (error) {
				console.error('Error fetching highlighted attestations:', error)
				toast.custom(
					t => (
						<Toast
							variant="error"
							content="Failed to load highlighted attestations"
							onClose={() => toast.dismiss(t)}
						/>
					),
					{
						position: 'bottom-right',
					},
				)
			}
		}
		fetchHighlightedAttestations()
	}, [artist.id])

	const handleHighlightAttestation = async (
		attestationIPFSCID: string,
		userId: string,
	) => {
		const loadingToastId = toast.custom(
			t => (
				<Toast
					variant="loading"
					content="Adding to highlights..."
					onClose={() => toast.dismiss(t)}
				/>
			),
			{
				position: 'bottom-right',
			},
		)

		try {
			const response = await fetch('/attestationHighlightsAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					userIdOrWallet: userId,
					attestations: [
						{
							attestationIPFSCID,
						},
					],
				}),
			})

			if (!response.ok) {
				throw new Error('Failed to highlight attestation')
			}

			const data = await response.json()
			if (data.highlightedAttestations) {
				setHighlightedAttestations(data.highlightedAttestations)
				toast.custom(
					t => (
						<Toast
							variant="success"
							content="This attestation has been highlighted"
							onClose={() => toast.dismiss(t)}
							textCTA="View in Highlights"
							onClickCTA={() => {
								const highlightsSection = document.querySelector(
									'#highlights-section',
								)
								if (highlightsSection) {
									highlightsSection.scrollIntoView({ behavior: 'smooth' })
								}
								toast.dismiss(t)
							}}
						/>
					),
					{
						id: loadingToastId,
						position: 'bottom-right',
					},
				)
			}
		} catch (error) {
			console.error('Error highlighting attestation:', error)
			toast.custom(
				t => (
					<Toast
						variant="error"
						content="Unable to update highlights due to a connection error. Please try again."
						onClose={() => toast.dismiss(t)}
					/>
				),
				{
					id: loadingToastId,
					position: 'bottom-right',
				},
			)
		}
	}

	const handleRemoveHighlight = async (
		attestationIPFSCID: string,
		userId: string,
	) => {
		const loadingToastId = toast.custom(
			t => (
				<Toast
					variant="loading"
					content="Removing from highlights..."
					onClose={() => toast.dismiss(t)}
				/>
			),
			{
				position: 'bottom-right',
			},
		)

		try {
			let previousHighlightedAttestations = [...highlightedAttestations]

			// Optimistically update the UI
			setHighlightedAttestations(prev => {
				return prev.filter(
					attestation =>
						attestation.references.attestationIPFSCID !== attestationIPFSCID,
				)
			})

			const response = await fetch('/attestationHighlightsAPI', {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					userIdOrWallet: userId,
					attestations: [
						{
							attestationIPFSCID,
						},
					],
				}),
			})

			if (!response.ok) {
				// Revert optimistic update on error
				setHighlightedAttestations(previousHighlightedAttestations)
				throw new Error('Failed to remove highlight')
			}

			const data = await response.json()
			if (data.highlightedAttestations) {
				// Ensure state is in sync with server
				setHighlightedAttestations(data.highlightedAttestations)

				// Use requestAnimationFrame to ensure state update has completed
				requestAnimationFrame(() => {
					toast.custom(
						t => (
							<Toast
								variant="success"
								content="This attestation has been removed from your highlighted moments"
								onClose={() => toast.dismiss(t)}
							/>
						),
						{
							id: loadingToastId,
							position: 'bottom-right',
						},
					)
				})
			}
		} catch (error) {
			console.error('Error removing highlight:', error)
			toast.custom(
				t => (
					<Toast
						variant="error"
						content="Unable to update highlights due to a connection error. Please try again."
						onClose={() => toast.dismiss(t)}
					/>
				),
				{
					id: loadingToastId,
					position: 'bottom-right',
				},
			)
		}
	}

	return (
		<div className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 lg:px-24">
			<ClientOnly>
				{
					// DynamicContext._currentValue is required to make sure DynamicContextProvider is available when this renders.
					// This is required to use the useDynamicContext hook in the component below
					// remix defaults to server side rendering, so we need to make sure the component is rendered on the client
					() => (
						<>
							<ArtistHeader
								artist={artist}
								origin={origin}
								setSignParams={setSignParams}
							/>
						</>
					)
				}
			</ClientOnly>
			<ArtistBio artist={artist} stats={stats} />
			<Suspense
				fallback={
					<>
						<Collections userId={artist.id} />
						<OffChain atomicLoreUrl={origin} timeline={[]} />
						<ProofOfExhibition exhibitions={[]} />
					</>
				}
			>
				<Await resolve={initialAttestations}>
					{resolvedAttestations => {
						// Update the state with resolved attestations when they're available
						useEffect(() => {
							setAttestations(resolvedAttestations)
						}, [resolvedAttestations])

						return (
							<>
								{attestations.filter(
									metadata =>
										metadata.template.slug == 'rollup' && metadata.event,
								).length ? (
									<FeaturedEvents
										events={
											attestations
												? attestations.filter(
														metadata =>
															metadata.template.slug == 'rollup' &&
															metadata.event,
													)
												: []
										}
									/>
								) : (
									''
								)}
								<Collections userId={artist.id} />
								<div id="highlights-section">
									<HighlightedAttestations
										highlightedAttestations={highlightedAttestations}
										user={artist}
										onRemoveHighlight={handleRemoveHighlight}
									/>
								</div>
								<OffChain
									atomicLoreUrl={origin}
									timeline={
										attestations
											? attestations.filter(
													metadata => metadata.template.slug != 'rollup',
												)
											: []
									}
									totalMetadata={
										attestations
											? attestations.filter(
													metadata => metadata.template.slug != 'rollup',
												).length
											: 0
									}
									onHighlightAttestation={handleHighlightAttestation}
									highlightedAttestations={highlightedAttestations}
								/>
								<ProofOfExhibition
									exhibitions={
										attestations
											? attestations.filter(
													metadata =>
														metadata.template.slug == 'proofOfExhibition',
												)
											: []
									}
								/>
							</>
						)
					}}
				</Await>
			</Suspense>
			{signParams.open && (
				<SignModal
					open={signParams.open}
					onClose={() => {
						setSignParams({ open: false })
					}}
					template={signParams.template}
					pendingSign={signParams.pendingSign}
					countersignEntry={signParams.countersignEntry}
					matchingAddress={signParams.matchingAddress}
					isValidSign={signParams.isValidSign}
					nft={signParams.nft}
					collection={signParams.collection}
					nftCollectionInfo={signParams.nftCollectionInfo}
					contractAddress={signParams.contractAddress}
					tokenId={signParams.tokenId}
					origin={signParams.origin}
					atomicLoreUrl={signParams.atomicLoreUrl}
					targetWallet={signParams.targetWallet}
				/>
			)}
		</div>
	)
}

const ArtistHeader = ({
	artist,
	origin,
	setSignParams,
}: {
	artist: User
	origin: string
	setSignParams: (params: SignParamsType) => void
}) => {
	const tooltipCopyWallet = useRef<TooltipRefProps>(null)
	const { primaryWallet } = useDynamicContext()
	const showWalletRecordButton =
		primaryWallet?.address &&
		artist.wallets.find(
			(wallet: { address: string }) => wallet.address === primaryWallet.address,
		) !== undefined

	return (
		<div className="flex flex-col justify-between px-0 lg:flex-row lg:items-center lg:px-12">
			{/* Artist Name, Avatar, and Wallet dropdown */}
			<div className="md:flex md:flex-row md:items-center">
				<Avatar
					size="xxLarge"
					avatar={{ src: artist?.userAvatar, name: artist?.username }}
					additionalClasses="dark:text-textPrimaryDark flex justify-center mb-6"
				/>
				<div className="flex flex-col justify-around gap-y-4 pl-0 md:pl-8 lg:justify-evenly">
					<TypographyDisplay
						size="small"
						isBold={true}
						additionalClasses="dark:text-textPrimaryDark mx-auto md:ml-0 md:mr-auto"
					>
						{artist.username}
					</TypographyDisplay>
					{artist.wallets && artist.wallets[0] && (
						<div className={'ml-auto mr-auto md:ml-0'}>
							{/* @ts-ignore */}
							<PillTabPopover
								title={
									artist.wallets[0] &&
									truncateStringInMiddle(artist.wallets[0].address)
								}
								leftIcon={''}
								rightIcon={<CaretDown className="ml-2"></CaretDown>}
								children={
									<>
										{(
											artist.wallets as { chain: string; address: string }[]
										).map((wallet: { chain: string; address: string }) => (
											<div
												id="copyWallet"
												key={wallet.address}
												className="flex items-center justify-between py-2"
											>
												<TypographyLabel
													size="medium"
													isBold={true}
													additionalClasses="text-textSecondaryDark dark:text-textSecondaryLight"
												>
													{truncateStringInMiddle(wallet.address)}
												</TypographyLabel>
												<CopyIcon
													className="text-textSecondaryDark dark:text-textSecondaryLight"
													onClick={() => {
														navigator.clipboard.writeText(wallet.address)
														tooltipCopyWallet.current?.open({
															anchorSelect: '#copyWallet',
															content: 'Copied!',
														})
														setTimeout(() => {
															tooltipCopyWallet.current?.close()
														}, 2000)
													}}
												/>
												<Tooltip imperativeModeOnly ref={tooltipCopyWallet} />
											</div>
										))}
									</>
								}
							/>
						</div>
					)}
				</div>
			</div>
			{/* Share and social links */}
			<div className="mx-auto mt-6 flex flex-col gap-4 lg:ml-auto lg:mr-0 lg:mt-0 xl:flex-row">
				<div className="flex flex-row justify-center gap-x-4">
					<a href={getShareUserProfileToXURL(artist, origin)} target="_blank">
						<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
							<ShareDotsIcon />
						</div>
					</a>
					{artist.socials.website && (
						<Link to={artist.socials.website} target="_blank">
							<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
								<WebsiteIcon />
							</div>
						</Link>
					)}
					{artist.socials.x && (
						<Link to={artist.socials.x} target="_blank">
							<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
								<TwitterIcon />
							</div>
						</Link>
					)}
					{artist.socials.instagram && (
						<Link to={artist.socials.instagram} target="_blank">
							<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
								<InstagramIcon />
							</div>
						</Link>
					)}
					{artist.socials.discord && (
						<Link to={artist.socials.discord} target="_blank">
							<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
								<DiscordIcon />
							</div>
						</Link>
					)}
					{artist.socials.opensea && (
						<Link to={artist.socials.opensea} target="_blank">
							<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
								<OpenSeaIcon />
							</div>
						</Link>
					)}
				</div>
				{
					// Only show the wallet record button if the user logged in is viewing their own profile page
					// DynamicContext._currentValue is required to ensure the context has contents
					DynamicContext &&
						DynamicContext._currentValue &&
						showWalletRecordButton && (
							<Button
								variant="primary"
								size="large"
								leftIcon={<PlusIcon />}
								onClick={() =>
									setSignParams({
										open: true,
										template: 'comment',
										onClose: () => {
											setSignParams({ open: false })
										},
										targetWallet: primaryWallet
											? {
													address: primaryWallet.address,
													chain: primaryWallet.chain,
												}
											: null,
										atomicLoreUrl: origin,
									})
								}
							>
								Wallet Record
							</Button>
						)
				}
			</div>
		</div>
	)
}

const ArtistBio = ({
	artist,
	stats,
}: {
	artist: artist
	stats: {
		totalCollections: number
		totalProofOfExhibitions: number
		totalAttestations: number
	}
}) => {
	return artist.shortDescription ||
		artist.longDescription ||
		artist.location ? (
		<div className="mt-24 px-0 md:flex md:flex-row md:justify-between lg:px-12">
			<div className="flex w-full flex-col gap-y-4">
				{' '}
				{/*set to md:w-1/2 when adding the stats table back*/}
				<TypographyHeading
					size="large"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{artist.shortDescription}
				</TypographyHeading>
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					{artist.longDescription}
				</TypographyParagraph>
				{artist.location && (
					<div className="flex flex-row items-center gap-x-1.5 text-textSecondaryLight dark:text-textSecondaryDark">
						<LocationIcon />
						<TypographyLabel
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							{artist.location}
						</TypographyLabel>
					</div>
				)}
			</div>
			{/*<div className="flex justify-center pt-6 sm:pl-4 md:w-1/2 md:justify-end md:pl-8 md:pt-0 lg:pl-0 ">
				<ArtistStatTable stats={stats} />
			</div>*/}
		</div>
	) : (
		''
	)
}

const ArtistStatTable = ({
	stats,
}: {
	stats: {
		totalCollections: number
		totalProofOfExhibitions: number
		totalAttestations: number
	}
}) => {
	return (
		<div className="h-fit w-full rounded-lg border border-textPrimaryLight dark:border-textPrimaryDark xl:w-3/4">
			<table className="min-w-full divide-y divide-textPrimaryLight dark:divide-textPrimaryDark">
				<thead>
					<tr className="divide-x divide-textPrimaryLight dark:divide-textPrimaryDark">
						<th
							scope="col"
							className="w-1/3 px-1 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark lg:px-4"
						>
							<TypographyCaps size="small">Collections</TypographyCaps>
						</th>
						<th
							scope="col"
							className="w-1/3 px-1 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark lg:px-4"
						>
							<TypographyCaps size="small">Exhibitions</TypographyCaps>
						</th>
						<th
							scope="col"
							className="w-1/3 px-1 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark lg:px-4"
						>
							<TypographyCaps size="small">Signatures</TypographyCaps>
						</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-textPrimaryLight dark:divide-textPrimaryDark">
					<tr className="divide-x divide-textPrimaryLight dark:divide-textPrimaryDark">
						<td className="w-1/3 whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{stats.totalCollections}
							</TypographyParagraph>
						</td>
						<td className="w-1/3 whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{stats.totalProofOfExhibitions}
							</TypographyParagraph>
						</td>
						<td className="w-1/3 whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
							<TypographyParagraph size="large" isBold={true}>
								{stats.totalAttestations}
							</TypographyParagraph>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	)
}

const FeaturedEvents = ({ events }: { events: any[] }) => {
	const [showAllEvents, setShowAllEvents] = useState(false)
	return (
		<div className="my-24 flex flex-col gap-y-4 px-0 lg:px-12">
			<TypographyHeading
				size="medium"
				isBold={true}
				additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
			>
				Event Highlights
			</TypographyHeading>
			<TypographyParagraph
				size="medium"
				additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
			>
				Discover the latest events showcasing this artist's work.
			</TypographyParagraph>
			<div className="-mx-4 flex flex-wrap">
				{events
					.slice(0, showAllEvents ? events.length : 3)
					.map((event: any) => {
						return (
							<div
								key={`${event.id}${event.references.attestationIPFSCID}`}
								className="mb-8 w-full px-4 sm:w-1/2 lg:w-1/3"
							>
								<InternalLink
									to={`/metadataProfile/event/${event.references.attestationIPFSCID}`}
									prefetch="none"
									additionalClasses="w-1/2 lg:w-1/3"
								>
									<EventCard
										name={event.event.eventTitle}
										image={event.event.eventImageUrl}
										dateStart={event.event.dateStart}
										dateEnd={event.event.dateEnd}
										cardAdditionalProps="h-full w-auto"
										containerAdditionalProps="relative w-full pt-[100%] bg-grey100"
										imageAdditionalProps="absolute top-0 left-0 w-full h-full object-cover"
									/>
								</InternalLink>
							</div>
						)
					})}
			</div>
			{!showAllEvents && events.length > 3 && (
				<div className="flex w-full justify-center">
					<IconButton
						additionalClasses="mt-12 h-10 w-14 border-solid px-4 py-2 rounded-full"
						disabled={false}
						icon={<MoreIcon />}
						onClick={() => {
							setShowAllEvents(true)
						}}
					/>
				</div>
			)}
		</div>
	)
}

const Collections = ({ userId }: { userId: string }) => {
	const [collectionsList, setCollectionsList] = useState<AttestationType[]>([])
	const [collectionsCount, setcollectionsCount] = useState(0)
	const [currentPage, setCurrentPage] = useState(0)
	const [sortCriteria, setSortCriteria] = useState({
		title: 'Newest',
		description: 'DESC',
	})
	const [loading, setLoading] = useState(false)
	const prevCurrentPage = useRef(currentPage)
	const prevSortOrder = useRef(sortCriteria.description)

	useEffect(() => {
		currentPage == 0 ? loadCollections() : setCurrentPage(0)
	}, [sortCriteria])

	useEffect(() => {
		loadCollections()
	}, [currentPage])

	useEffect(() => {
		setLoading(false)
	}, [collectionsList])

	const loadCollections = async () => {
		setLoading(true)
		prevCurrentPage.current = currentPage
		prevSortOrder.current = sortCriteria.description
		setTimeout(async () => {
			if (
				prevCurrentPage.current == currentPage &&
				prevSortOrder.current == sortCriteria.description
			) {
				try {
					const options = {
						method: 'GET',
						headers: { 'Content-Type': 'application/json' },
					}
					const response = await fetch(
						`/userAttestationsAPI?attestationType=completedAttestations&page=${currentPage}&pageSize=10&sortOrder=${sortCriteria.description}&filterCriteria=collection&userId=${userId}&includeRevoked=false`,
						options,
					)
					const collectionsResponse = (await response.json()) as {
						attestations: AttestationType[]
						count: number
					}

					let attestations = collectionsResponse.attestations

					try {
						let userCollectionsOrder
						if (import.meta.env.VITE_USER_COLLECTIONS_ORDER) {
							userCollectionsOrder = JSON.parse(
								import.meta.env.VITE_USER_COLLECTIONS_ORDER,
							)
							if (userCollectionsOrder[userId]) {
								attestations = sortByCustomCriteria(
									attestations,
									userCollectionsOrder[userId],
								)
							}
						}
					} catch (e) {
						// Ignore the error if no valid variable is found
						console.error(e)
					}

					setcollectionsCount(collectionsResponse.count)
					setCollectionsList(attestations)
				} catch (e) {
					console.error(e)
				}
			}
		}, 300)
	}

	const sortByCustomCriteria = (
		attestations: AttestationType[],
		cids: string[],
	): AttestationType[] => {
		const orderedMatches: AttestationType[] = []
		const usedSet = new Set<AttestationType>()

		// Step 1–2: Add first matching attestation per cid (in cid order)
		for (const cid of cids) {
			const match = attestations.find(
				attestation =>
					attestation.references.attestationIPFSCID === cid &&
					!usedSet.has(attestation),
			)
			if (match) {
				orderedMatches.push(match)
				usedSet.add(match)
			}
		}

		// Step 3: Add remaining attestations that were not already added
		const remainingObjects = attestations.filter(
			attestation => !usedSet.has(attestation),
		)

		return [...orderedMatches, ...remainingObjects]
	}

	const sort = [
		{ title: 'Newest', description: 'DESC' },
		{ title: 'Oldest', description: 'ASC' },
	]

	return collectionsCount > 0 ? (
		<div className="my-24 flex w-full flex-col gap-y-4 px-0 lg:px-12">
			<div className="w-full justify-between sm:flex sm:flex-row">
				<div className="flex flex-1 items-center gap-x-4">
					<TypographyHeading
						size="medium"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Onchain Collections
					</TypographyHeading>
					<Counter size="small" children={collectionsCount} />
				</div>
				<div className="mt-3 flex flex-1 items-center justify-start gap-x-4 sm:mt-0 sm:justify-end">
					{/*<SearchInput
						name="Search"
						placeholder="Search"
						showShadow={false}
						value={filterValue}
						setValue={newVal => {
							filterCollections(newVal)
						}}
					/>*/}
					<Filter
						type="Sort"
						options={sort}
						onChange={newVal => setSortCriteria(newVal)}
						value={sortCriteria}
					></Filter>
				</div>
			</div>
			<div className="pt-6">
				<CollectionsTable
					data={collectionsList}
					showButton={false}
					loading={loading}
				/>
			</div>
			{collectionsCount > 10 && (
				<div>
					<Pagination
						index={currentPage}
						count={collectionsCount}
						setIndex={newPage => setCurrentPage(newPage)}
					/>
				</div>
			)}
		</div>
	) : (
		''
	)
}
